import { useTable } from "@refinedev/core";

import {
  ErrorBox,
  FilterBar,
  Loading,
  PageHeader,
  Pagination,
  SearchInput,
} from "@/components/Page";
import { EmptyRow, Sub, Table, Td, Th } from "@/components/Table";
import { formatDateTime } from "@/lib/format";
import { filterValue } from "@/lib/query";

interface AuditRow extends Record<string, unknown> {
  id: string;
  created_at: string;
  actor: string | null;
  action: string;
  entity: string | null;
  entity_id: string | null;
  reason: string | null;
  ip: string | null;
}

export function AuditPage() {
  const { result, tableQuery, filters, setFilters, currentPage, setCurrentPage, pageSize } =
    useTable<AuditRow>({
      resource: "audit-log",
      sorters: { initial: [{ field: "created_at", order: "desc" }] },
    });

  const ustawFiltr = (field: string, value: string) =>
    setFilters([{ field, operator: "eq", value }], "merge");

  return (
    <>
      <PageHeader
        title="Audyt"
        description="Każda akcja wykonana w panelu. Tylko do odczytu - dziennik, który da się poprawić, nie jest dziennikiem."
      />

      <FilterBar>
        <SearchInput
          label="Akcja"
          placeholder="np. order.refund"
          value={filterValue(filters, "action")}
          onChange={(value) => ustawFiltr("action", value)}
        />
        <SearchInput
          label="Obiekt"
          placeholder="np. order"
          value={filterValue(filters, "entity")}
          onChange={(value) => ustawFiltr("entity", value)}
        />
      </FilterBar>

      <ErrorBox error={tableQuery.error} />

      {tableQuery.isLoading ? (
        <Loading />
      ) : (
        <>
          <Table>
            <thead>
              <tr>
                <Th>Kiedy</Th>
                <Th>Kto</Th>
                <Th>Akcja</Th>
                <Th>Obiekt</Th>
                <Th>Powód</Th>
                <Th>Adres IP</Th>
              </tr>
            </thead>
            <tbody>
              {result.data.length === 0 && (
                <EmptyRow colSpan={6}>Brak wpisów pasujących do tych filtrów.</EmptyRow>
              )}
              {result.data.map((entry) => (
                <tr key={entry.id}>
                  <Td nowrap>{formatDateTime(entry.created_at)}</Td>
                  <Td>{entry.actor ?? "-"}</Td>
                  <Td>
                    {/* Nazwa akcji jest identyfikatorem, nie zdaniem. */}
                    <span className="font-mono text-sm">{entry.action}</span>
                  </Td>
                  <Td>
                    {entry.entity ?? "-"}
                    {entry.entity_id && <Sub>{entry.entity_id}</Sub>}
                  </Td>
                  <Td>
                    <span className="text-ink-soft">{entry.reason ?? "-"}</span>
                  </Td>
                  <Td numeric>{entry.ip ?? "-"}</Td>
                </tr>
              ))}
            </tbody>
          </Table>

          <Pagination
            page={currentPage}
            pageSize={pageSize}
            total={result.total ?? 0}
            onChange={setCurrentPage}
          />
        </>
      )}
    </>
  );
}
